import type { FilterExpression, QueryDiagnostic } from '@aeppic/query/contracts'

export function validateFilterExpression(expression: FilterExpression, path: readonly (string | number)[], maxDepth: number): QueryDiagnostic[] {
  const diagnostics: QueryDiagnostic[] = []
  validateJsonValue(expression, path, new Set(), diagnostics)

  if (diagnostics.length === 0) {
    validateExpressionNode(expression, path, 0, maxDepth, diagnostics)
  }

  return diagnostics
}

function validateJsonValue(value: unknown, path: readonly (string | number)[], ancestors: Set<object>, diagnostics: QueryDiagnostic[]) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      diagnostics.push(error('non-json-value', 'Query expressions require finite JSON numbers', path))
    }
    return
  }

  if (typeof value !== 'object') {
    diagnostics.push(error('non-json-value', `Query expressions cannot contain ${typeof value} values`, path))
    return
  }

  if (ancestors.has(value)) {
    diagnostics.push(error('expression-cycle', 'Query expressions cannot contain cycles', path))
    return
  }

  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
    diagnostics.push(error('non-json-value', 'Query expressions can contain only plain objects and arrays', path))
    return
  }

  ancestors.add(value)
  for (const [key, child] of Object.entries(value)) {
    validateJsonValue(child, [...path, Array.isArray(value) ? Number(key) : key], ancestors, diagnostics)
  }
  ancestors.delete(value)
}

function validateExpressionNode(expression: FilterExpression, path: readonly (string | number)[], depth: number, maxDepth: number, diagnostics: QueryDiagnostic[]) {
  if (depth > maxDepth) {
    diagnostics.push(error('expression-depth-exceeded', `Query expression exceeds maximum depth ${maxDepth}`, path))
    return
  }

  if (!expression || typeof expression !== 'object' || typeof (expression as FilterExpression).kind !== 'string') {
    diagnostics.push(error('invalid-expression', 'Expected a filter expression object', path))
    return
  }

  switch (expression.kind) {
    case 'group':
      if (expression.operator !== 'AND' && expression.operator !== 'OR') {
        diagnostics.push(error('invalid-group-operator', 'Group operator must be AND or OR', [...path, 'operator']))
      }
      if (!Array.isArray(expression.children)) {
        diagnostics.push(error('invalid-group-children', 'Group children must be an array', [...path, 'children']))
        return
      }
      expression.children.forEach((child, index) => validateExpressionNode(child, [...path, 'children', index], depth + 1, maxDepth, diagnostics))
      return
    case 'field':
      if (!expression.field || typeof expression.field !== 'string') {
        diagnostics.push(error('invalid-field', 'Field expression requires a field path', [...path, 'field']))
      }
      if (!expression.operator || typeof expression.operator !== 'string') {
        diagnostics.push(error('invalid-operator', 'Field expression requires an operator', [...path, 'operator']))
      }
      return
    case 'text-search':
      if (typeof expression.text !== 'string') {
        diagnostics.push(error('invalid-text-search', 'Text search requires text', [...path, 'text']))
      }
      return
    case 'relation':
      validateExpressionNode(expression.expression, [...path, 'expression'], depth + 1, maxDepth, diagnostics)
      return
    case 'named-fragment':
      if (!expression.fragment || typeof expression.fragment.id !== 'string' || !expression.fragment.id) {
        diagnostics.push(error('invalid-fragment-id', 'Named fragment requires a non-empty id', [...path, 'fragment', 'id']))
      }
      if (!expression.fragment || typeof expression.fragment.version !== 'string' || !expression.fragment.version) {
        diagnostics.push(error('invalid-fragment-version', 'Named fragment requires a non-empty version', [...path, 'fragment', 'version']))
      }
      if (expression.parameters != null && (Array.isArray(expression.parameters) || typeof expression.parameters !== 'object')) {
        diagnostics.push(error('invalid-fragment-parameters', 'Named fragment parameters must be an object', [...path, 'parameters']))
      }
      return
    default:
      diagnostics.push(error('unknown-expression-kind', `Unknown query expression kind ${(expression as { kind: string }).kind}`, [...path, 'kind']))
  }
}

function error(code: string, message: string, path: readonly (string | number)[]): QueryDiagnostic {
  return { code, message, severity: 'error', path }
}
